import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarClock, Loader2, Search, CheckCircle2, X, UserRound, UserCheck, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { AddProspectAccountInline } from './AddProspectAccountInline';
import { useUIPreview } from '../contexts/UIPreviewContext';
import { useActivityBar } from '../contexts/ActivityBarContext';
import { modalV2OverlayClass } from '../utils/modalUiV2';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CALL_PURPOSE = 'Price Enquiry';
const RECENT_ASSIGNEES_KEY = 'c30_scheduled_call_recent_assignees';
const RECENT_ASSIGNEES_MAX = 6;

/**
 * "Schedule Call" modal — a future-dated counterpart of AddCallToCrmModal.
 * Creates a Zoho Call record with Call_Status = Scheduled and an assignee
 * (a C30 user whose email maps to a Zoho CRM user). The assignee then sees
 * the call in their in-app Notification Bar until they mark it Completed.
 */
export const ScheduleCallModal = ({ isOpen, onClose, onScheduled = null, resumeSnapshot = null }) => {
  const { modalUiV2 } = useUIPreview();
  const activityBar = useActivityBar();
  const activityIdRef = useRef(resumeSnapshot?.activityId || null);
  // Default the schedule date to "tomorrow 09:00" in the browser's local zone.
  const _tomorrow9am = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const [scheduleFor, setScheduleFor] = useState(_tomorrow9am());

  // Account picker (same pattern as AddCallToCrmModal)
  const [accountQuery, setAccountQuery] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Contacts
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactId, setContactId] = useState('');

  // Purposes
  const [purposes, setPurposes] = useState([]);
  const [purposesLoading, setPurposesLoading] = useState(false);
  const [purpose, setPurpose] = useState('');

  const [description, setDescription] = useState('');
  const [callTag, setCallTag] = useState('');
  const [recentTags, setRecentTags] = useState([]);

  // Assignee search
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [assigneeResults, setAssigneeResults] = useState([]);
  const [assigneeLoading, setAssigneeLoading] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState(null);  // {id,email,display_name}
  const [recentAssignees, setRecentAssignees] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  // Inline "Add new prospect account" toggle
  const [showAddAccount, setShowAddAccount] = useState(false);

  const resetAll = () => {
    setScheduleFor(_tomorrow9am());
    setAccountQuery('');
    setAccounts([]);
    setSelectedAccount(null);
    setContacts([]);
    setContactId('');
    setPurpose(DEFAULT_CALL_PURPOSE);
    setDescription('');
    setCallTag('');
    setAssigneeQuery('');
    setAssigneeResults([]);
    setSelectedAssignee(null);
    setShowAddAccount(false);
  };

  // Load purposes + recent assignees when modal opens
  useEffect(() => {
    if (!isOpen) return;
    resetAll();
    // Recent assignees from localStorage
    try {
      const raw = localStorage.getItem(RECENT_ASSIGNEES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setRecentAssignees(parsed.slice(0, RECENT_ASSIGNEES_MAX));
      }
    } catch (_) { /* ignore */ }
    (async () => {
      setPurposesLoading(true);
      try {
        const [purposeRes, tagRes] = await Promise.all([
          api.get(`${API}/zoho/call-purposes`),
          api.get(`${API}/zoho/recent-tags`).catch(() => ({ data: { tags: [] } })),
        ]);
        const list = purposeRes.data.purposes || [];
        setPurposes(list);
        if (list.includes(DEFAULT_CALL_PURPOSE)) setPurpose(DEFAULT_CALL_PURPOSE);
        setRecentTags(tagRes.data?.tags || []);
      } catch (_) {
        // non-fatal
      } finally {
        setPurposesLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Modal UI v2 — resume from a minimized "Open Activities" tab.
  useEffect(() => {
    if (!isOpen || !resumeSnapshot) return;
    if (resumeSnapshot.selectedAccount) {
      setSelectedAccount(resumeSnapshot.selectedAccount);
      setAccountQuery(resumeSnapshot.selectedAccount.name || '');
    }
    if (resumeSnapshot.contactId) setContactId(resumeSnapshot.contactId);
    if (resumeSnapshot.purpose) setPurpose(resumeSnapshot.purpose);
    if (resumeSnapshot.description) setDescription(resumeSnapshot.description);
    if (resumeSnapshot.callTag) setCallTag(resumeSnapshot.callTag);
    if (resumeSnapshot.selectedAssignee) {
      setSelectedAssignee(resumeSnapshot.selectedAssignee);
      setAssigneeQuery(resumeSnapshot.selectedAssignee.display_name || resumeSnapshot.selectedAssignee.email || '');
    }
    if (resumeSnapshot.scheduleFor) setScheduleFor(resumeSnapshot.scheduleFor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resumeSnapshot]);

  // Register/refresh the live Activity Bar entry once an account is picked.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !selectedAccount?.id) return;
    if (!activityIdRef.current) activityIdRef.current = `schedule-call-${selectedAccount.id}-${Date.now()}`;
    const title = `Schedule Call — ${selectedAccount.name}`;
    const snapshot = { activityId: activityIdRef.current, selectedAccount, contactId, purpose, description, callTag, selectedAssignee, scheduleFor };
    activityBar.openActivity({ type: 'schedule_call', activityId: activityIdRef.current, title, subtitle: 'Schedule Call', snapshot });
    activityBar.logRecentView({ type: 'schedule_call', activityId: activityIdRef.current, title, subtitle: 'Schedule Call', snapshot });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, selectedAccount?.id]);

  // Keep the live snapshot fresh as the rep fills in the form.
  useEffect(() => {
    if (!isOpen || !modalUiV2 || !activityIdRef.current) return;
    activityBar.updateActiveSnapshot({
      activityId: activityIdRef.current, selectedAccount, contactId, purpose, description, callTag, selectedAssignee, scheduleFor,
    }, { title: `Schedule Call — ${selectedAccount?.name || ''}`, subtitle: 'Schedule Call' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalUiV2, selectedAccount, contactId, purpose, description, callTag, selectedAssignee, scheduleFor]);

  // Modal UI v2 keeps the header interactive above the overlay — lock page scroll.
  useEffect(() => {
    if (!isOpen || !modalUiV2) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [isOpen, modalUiV2]);
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const q = accountQuery;
      if (!q || q.trim().length < 2) { setAccounts([]); return; }
      setAccountLoading(true);
      api.get(`${API}/zoho/search-accounts`, { params: { q } })
        .then(r => setAccounts(r.data.accounts || []))
        .catch(() => setAccounts([]))
        .finally(() => setAccountLoading(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountQuery, isOpen]);

  // Auto-load contacts when an account is selected
  useEffect(() => {
    if (!selectedAccount?.id) return;
    setContacts([]);
    setContactId('');
    setContactsLoading(true);
    api.get(`${API}/zoho/account/${selectedAccount.id}/contacts`)
      .then(r => setContacts(r.data.contacts || []))
      .catch(() => setContacts([]))
      .finally(() => setContactsLoading(false));
  }, [selectedAccount]);

  // Debounced assignee search
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => {
      const q = assigneeQuery;
      if (!q || q.trim().length < 2) { setAssigneeResults([]); return; }
      setAssigneeLoading(true);
      api.get(`${API}/users/search-light`, { params: { q } })
        .then(r => setAssigneeResults(r.data.users || []))
        .catch(() => setAssigneeResults([]))
        .finally(() => setAssigneeLoading(false));
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigneeQuery, isOpen]);

  const pickAssignee = (user) => {
    setSelectedAssignee(user);
    setAssigneeQuery(user.display_name || user.email);
    setAssigneeResults([]);
  };

  const saveRecentAssignee = (user) => {
    try {
      const trimmed = [
        { id: user.id, email: user.email, display_name: user.display_name },
        ...recentAssignees.filter(r => r.email !== user.email),
      ].slice(0, RECENT_ASSIGNEES_MAX);
      setRecentAssignees(trimmed);
      localStorage.setItem(RECENT_ASSIGNEES_KEY, JSON.stringify(trimmed));
    } catch (_) { /* ignore */ }
  };

  const handleSubmit = async () => {
    if (!selectedAccount?.id) return toast.error('Pick a customer account first');
    if (!purpose) return toast.error('Pick a call purpose');
    if (!selectedAssignee?.email) return toast.error('Pick an assignee');
    if (!scheduleFor) return toast.error('Pick a date & time');

    setSubmitting(true);
    try {
      const res = await api.post(`${API}/zoho/schedule-call`, {
        account_id: selectedAccount.id,
        contact_id: contactId || null,
        call_start_time: scheduleFor,
        call_duration_minutes: 5,
        call_purpose: purpose,
        subject: `${purpose} — ${selectedAccount.name}`,
        description: description || null,
        assignee_email: selectedAssignee.email,
      });
      // Save tag if provided
      const callId = res.data?.call_id;
      if (callId && callTag.trim()) {
        api.post(`${API}/zoho/call-tags`, { call_id: callId, tag: callTag.trim() }).catch(() => {});
      }
      saveRecentAssignee(selectedAssignee);
      toast.success(`Scheduled for ${selectedAssignee.display_name || selectedAssignee.email}`);
      window.dispatchEvent(new Event('c30:calls-changed'));
      onScheduled?.();
      resetAll();
      if (modalUiV2) activityBar.discardActive();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to schedule call');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const fieldCardClass = "rounded-xl border border-gray-200 bg-white shadow-sm p-3 transition-shadow hover:shadow-md";
  const inputClass = "rounded-lg border-2 border-gray-300 bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-200 h-10";

  return (
    <div
      className={`fixed left-0 right-0 bottom-0 flex items-center justify-center p-3 ${modalV2OverlayClass(modalUiV2)}`}
      style={modalUiV2 ? { top: activityBar.headerHeight, zIndex: 45 } : { top: 0, zIndex: 80 }}
      data-testid="schedule-call-modal"
    >
      <div className="bg-gradient-to-br from-purple-50/40 via-white to-purple-50/20 rounded-3xl shadow-2xl w-full max-w-2xl overflow-y-auto border-2 border-orange-300 flex flex-col" style={{ maxHeight: modalUiV2 ? `calc(100vh - ${activityBar.headerHeight}px - 32px)` : '95vh' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 rounded-t-3xl flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)' }}>
              <CalendarClock className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-extrabold text-gray-900 tracking-tight leading-tight truncate">Schedule Call</h2>
              <p className="text-[11px] text-gray-500 truncate">Book a call into a teammate's Zoho schedule</p>
            </div>
          </div>
          {/* Inline "Scheduled for" picker — saves a row so the form fits without
              scrolling on iPad. Hidden on very narrow widths; mobile gets its own field below. */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
            <Input
              type="datetime-local"
              value={scheduleFor}
              onChange={(e) => setScheduleFor(e.target.value)}
              className="rounded-2xl border-2 border-purple-200 bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-200 h-10 text-sm w-[240px] px-4"
              data-testid="schedule-datetime-input"
              aria-label="Scheduled for"
            />
          </div>
          <button onClick={() => { if (modalUiV2) activityBar.discardActive(); onClose?.(); }} className="text-gray-400 hover:text-gray-700 rounded-full p-1.5 hover:bg-gray-100 flex-shrink-0" data-testid="schedule-modal-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {/* Mobile-only date field */}
          <div className="sm:hidden">
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Scheduled for</Label>
            <Input
              type="datetime-local"
              value={scheduleFor}
              onChange={(e) => setScheduleFor(e.target.value)}
              className={`mt-1 ${inputClass}`}
              data-testid="schedule-datetime-input-mobile"
            />
          </div>

          {/* Customer picker */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Customer</Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                value={accountQuery}
                onChange={(e) => { setAccountQuery(e.target.value); if (selectedAccount) setSelectedAccount(null); }}
                placeholder="Type any part of the customer name (e.g. huw tel)"
                className={`flex-1 ${inputClass}`}
                data-testid="schedule-account-search"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name="schedule-account-q"
                role="presentation"
                aria-autocomplete="list"
              />
              <Button type="button" disabled={accountLoading} className="rounded-xl h-11 w-11 p-0 bg-purple-600 hover:bg-purple-700">
                {accountLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Search className="w-4 h-4 text-white" />}
              </Button>
            </div>
            {!selectedAccount && (accountLoading || accounts.length > 0) && (
              <div className="border border-gray-200 rounded-xl divide-y max-h-56 overflow-y-auto mt-2 bg-white shadow-inner">
                {accountLoading ? (
                  <div className="p-3 text-center text-gray-500 text-sm">Searching…</div>
                ) : accounts.length === 0 ? (
                  <div className="p-3 text-center text-gray-500 text-sm">No accounts found</div>
                ) : (
                  accounts.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => { setSelectedAccount(a); setAccountQuery(a.name); }}
                      className="w-full text-left p-3 hover:bg-purple-50 transition-colors"
                      data-testid={`schedule-account-option-${a.id}`}
                    >
                      <div className="font-semibold text-sm text-gray-900">{a.name}</div>
                      <div className="text-xs text-gray-500">
                        {[a.city, a.customer_code ? `#${a.customer_code}` : null].filter(Boolean).join(' · ')}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
            {selectedAccount && (
              <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-semibold flex-1">{selectedAccount.name}</span>
                <button
                  type="button"
                  onClick={() => { setSelectedAccount(null); setAccountQuery(''); setContacts([]); setContactId(''); }}
                  className="text-[11px] text-gray-400 hover:text-gray-600 underline"
                >
                  change
                </button>
              </div>
            )}

            {/* "Add prospect account" toggle — purple-themed to match the rest of this modal */}
            {!selectedAccount && !showAddAccount && (
              <button
                type="button"
                onClick={() => setShowAddAccount(true)}
                className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-purple-700 hover:text-purple-800"
                data-testid="schedule-add-account-toggle"
              >
                <Building2 className="w-3.5 h-3.5" />
                Can't find them? Add as a new prospect
              </button>
            )}
            {showAddAccount && (
              <AddProspectAccountInline
                themeColor="purple"
                onCancel={() => setShowAddAccount(false)}
                onCreated={({ account, contact }) => {
                  if (account?.id) {
                    setSelectedAccount({ id: account.id, name: account.name });
                    setAccountQuery(account.name);
                  }
                  if (contact?.id) {
                    setContacts([{
                      id: contact.id,
                      full_name: contact.full_name,
                      first_name: contact.first_name,
                      last_name: contact.last_name,
                      email: contact.email,
                      phone: contact.phone,
                      mobile: contact.mobile,
                    }]);
                    setContactId(contact.id);
                  }
                  setShowAddAccount(false);
                }}
              />
            )}
          </div>

          {/* Contact picker (optional) */}
          {selectedAccount && (
            <div className={fieldCardClass}>
              <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1">
                <UserRound className="w-3.5 h-3.5" /> Contact (optional)
              </Label>
              {contactsLoading ? (
                <div className="text-xs text-gray-500 mt-3">Loading contacts…</div>
              ) : contacts.length === 0 ? (
                <div className="text-xs text-gray-500 mt-3">No contacts on this account in Zoho.</div>
              ) : (
                <Select value={contactId} onValueChange={setContactId}>
                  <SelectTrigger className={`mt-1.5 ${inputClass}`} data-testid="schedule-contact-select">
                    <SelectValue placeholder="No specific contact" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-72 z-[120] rounded-xl border-gray-200 shadow-xl">
                    {contacts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name || c.email || c.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Purpose */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700">Call Purpose *</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger className={`mt-1.5 ${inputClass}`} data-testid="schedule-purpose-select">
                <SelectValue placeholder={purposesLoading ? 'Loading…' : 'Choose a purpose'} />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72 z-[120] rounded-xl border-gray-200 shadow-xl">
                {purposes.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-400">No purposes found</div>
                ) : purposes.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Assignee picker */}
          <div className={fieldCardClass}>
            <Label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1">
              <UserCheck className="w-3.5 h-3.5" /> Assign to *
            </Label>
            {/* Recent chips */}
            {recentAssignees.length > 0 && !selectedAssignee && (
              <div className="flex flex-wrap gap-1.5 mt-2 mb-1">
                {recentAssignees.map((r) => (
                  <button
                    key={r.email}
                    type="button"
                    onClick={() => pickAssignee(r)}
                    className="text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 rounded-full px-2.5 py-1 hover:bg-purple-100"
                    data-testid={`schedule-recent-assignee-${r.email}`}
                  >
                    {r.display_name || r.email}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-1.5">
              <Input
                value={assigneeQuery}
                onChange={(e) => { setAssigneeQuery(e.target.value); if (selectedAssignee) setSelectedAssignee(null); }}
                placeholder="Search by name or email"
                className={`flex-1 ${inputClass}`}
                data-testid="schedule-assignee-search"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                name="schedule-assignee-q"
                role="presentation"
                aria-autocomplete="list"
              />
              <Button type="button" disabled={assigneeLoading} className="rounded-xl h-11 w-11 p-0 bg-purple-600 hover:bg-purple-700">
                {assigneeLoading ? <Loader2 className="w-4 h-4 animate-spin text-white" /> : <Search className="w-4 h-4 text-white" />}
              </Button>
            </div>
            {!selectedAssignee && (assigneeLoading || assigneeResults.length > 0) && (
              <div className="border border-gray-200 rounded-xl divide-y max-h-48 overflow-y-auto mt-2 bg-white shadow-inner">
                {assigneeLoading ? (
                  <div className="p-3 text-center text-gray-500 text-sm">Searching…</div>
                ) : assigneeResults.length === 0 ? (
                  <div className="p-3 text-center text-gray-500 text-sm">No users found</div>
                ) : (
                  assigneeResults.map((u) => (
                    <button
                      key={u.email}
                      type="button"
                      onClick={() => pickAssignee(u)}
                      className="w-full text-left p-3 hover:bg-purple-50 transition-colors"
                      data-testid={`schedule-assignee-option-${u.email}`}
                    >
                      <div className="font-semibold text-sm text-gray-900">{u.display_name || u.email}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </button>
                  ))
                )}
              </div>
            )}
            {selectedAssignee && (
              <div className="mt-2 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-semibold flex-1">{selectedAssignee.display_name || selectedAssignee.email}</span>
                <button
                  type="button"
                  onClick={() => { setSelectedAssignee(null); setAssigneeQuery(''); }}
                  className="text-[11px] text-gray-400 hover:text-gray-600 underline"
                >
                  change
                </button>
              </div>
            )}

            {/* Optional note-to-user. Appears under the call block in the
                assignee's notification bar so they know what the call is for. */}
            <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Note to user (optional)</Label>
              <textarea
                className="w-full rounded-lg border-2 border-gray-300 bg-white focus:border-purple-400 px-3 py-2 text-sm resize-y min-h-[48px] focus:outline-none focus:ring-2 focus:ring-purple-200 mt-1"
                placeholder="Quick guidance, e.g. “Ask about the new Q3 range before pushing the quote.”"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="schedule-notes-input"
              />
            </div>

            {/* Call Tag — short label shown as a pill on call cards */}
            <div className="mt-3 pt-3 border-t border-dashed border-gray-200">
              <Label className="text-[11px] font-bold uppercase tracking-wider text-gray-600">Call Tag (optional)</Label>
              <p className="text-[10px] text-gray-400 mb-1.5">Short label shown as a pill on the call card — hover the pill to see the note.</p>
              <Input
                value={callTag}
                onChange={(e) => setCallTag(e.target.value)}
                placeholder="e.g. Q3 Range, Price Review, New Account"
                className="rounded-lg border-2 border-gray-300 bg-white focus:border-purple-400 h-9 text-sm"
                maxLength={80}
                data-testid="schedule-tag-input"
              />
              {recentTags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold self-center mr-1">Recent</span>
                  {recentTags.map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setCallTag(callTag === t ? '' : t)}
                      className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
                        callTag === t
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100'
                      }`}
                      data-testid={`schedule-recent-tag-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              )}
            </div>

          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-2 sticky bottom-0 bg-gradient-to-t from-white via-white to-white/60 -mx-4 px-4 py-3 border-t border-orange-100 mt-2">
            <Button variant="outline" onClick={() => { if (modalUiV2) activityBar.discardActive(); onClose?.(); }} className="rounded-lg h-10 px-4 border-2" data-testid="schedule-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedAccount || !purpose || !selectedAssignee}
              className="rounded-lg h-10 px-5 text-white font-semibold shadow-md hover:shadow-lg transition-all"
              style={{ background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)' }}
              data-testid="schedule-submit-btn"
            >
              {submitting ? (<><Loader2 className="w-4 h-4 animate-spin mr-2" />Scheduling…</>) : (<><CalendarClock className="w-4 h-4 mr-2" />Schedule Call</>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleCallModal;
